import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { useLookup } from "../context/LookupContext.js";
import { useToast } from "../context/ToastContext.js";

import { ADMIN_ROLES as SUPER_HR } from "@hrms/types";
import { X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { AsyncButton } from "../components/ui/async-button.js";
import { Button } from "../components/ui/button.js";
import { SelectField } from "../components/ui/select-field.js";
import { Input } from "../components/ui/input.js";
import { usePageBack } from "../context/PageBackContext.js";
import { LeaveChangeHistory } from "../components/LeaveChangeHistory.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table.js";
import { useHorizontalScrollEdges } from "../hooks/useHorizontalScrollEdges.js";

interface TypeConfig {
  id: string; code: string; trackBalance: boolean;
  genderRestriction: string | null; maritalStatusRestriction: string | null; isProbationBlocked: boolean;
  isNoticeBlocked: boolean; isPipBlocked: boolean; forceWfh: boolean;
  maxDaysPerRequest: number | null; attachmentRequiredAboveDays: number | null; requiresApproverRole: string;
  isEnabled: boolean;
}

interface PolicyTier { id?: string; minTenureMonths: number; daysAllowed: number }

interface LeavePolicy {
  id: string; name: string; leaveType: string; country: string;
  employeeType: string; daysAllowed: number | string; carryForward: number | string;
  minNoticeDays: number; isActive: boolean; proRata: boolean;
  effectiveFrom: string; effectiveTo: string | null;
  changedBy: string | null; changeNote: string | null;
  tiers: PolicyTier[];
}

interface Blackout {
  id: string; name: string; country: string;
  startMonth: number; startDay: number; endMonth: number; endDay: number;
  year: number | null; leaveTypeCodes: string; isActive: boolean;
}

interface Department { id: string; name: string; code: string; wfhEligible: boolean }
interface Holiday { id: string; name: string; date: string; year: number; isNational: boolean; isOptional: boolean; country: string }
interface Employee { id: string; firstName: string; lastName: string; employeeCode: string }

const TABS = ["Leave Types", "Allocation Rules", "Blackout Periods", "WFH & Departments", "National Holidays & Comoff", "Employee Balances", "History"] as const;
type Tab = typeof TABS[number];

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const card: React.CSSProperties = {
  background: "var(--color-surface-0, #fff)",
  border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-lg)",
  padding: 20,
};

const inp: React.CSSProperties = {
  width: "100%", padding: "7px 10px",
  border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)",
  fontSize: 13, outline: "none", background: "var(--color-surface-0)", boxSizing: "border-box",
};

const lbl: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "var(--color-surface-600)", display: "block", marginBottom: 4,
};

const btn: React.CSSProperties = {
  padding: "8px 16px", border: "none", borderRadius: "var(--radius-md)",
  background: "var(--color-brand-500)", color: "#fff",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
};

const btnSm: React.CSSProperties = { ...btn, padding: "4px 10px", fontSize: 11 };

const ghost: React.CSSProperties = {
  ...btn, background: "var(--color-surface-100)", color: "var(--color-surface-700)",
};

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer",
        background: checked ? "var(--color-brand-500)" : "var(--color-surface-300)",
        position: "relative", flexShrink: 0, transition: "background 0.15s",
      }}
    >
      <span style={{
        position: "absolute", top: 3, left: checked ? 18 : 3,
        width: 14, height: 14, borderRadius: "50%", background: "#fff",
        transition: "left 0.15s",
      }} />
    </button>
  );
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: color + "22", color }}>
      {label}
    </span>
  );
}

// ── Tab 1: Leave Types ────────────────────────────────────────────────────────

const TYPE_FIELD_LABELS: [keyof TypeConfig, string][] = [
  ["isEnabled", "Enabled"], ["trackBalance", "Track Balance"], ["genderRestriction", "Gender"],
  ["maritalStatusRestriction", "Marital Status"], ["isProbationBlocked", "Probation Block"],
  ["isNoticeBlocked", "Notice Block"], ["isPipBlocked", "PIP Block"], ["forceWfh", "Force WFH"],
  ["maxDaysPerRequest", "Max Days/Req"], ["attachmentRequiredAboveDays", "Attach Above"],
  ["requiresApproverRole", "Approver Role"],
];

function fmtTypeValue(v: unknown): string {
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (v === null || v === undefined || v === "") return "Any";
  return String(v).replace(/_/g, " ");
}

// "Enabled: On → Off, Max Days/Req: Any → 5" -- what the admin actually changed on the row.
function describeTypeChanges(before: TypeConfig | undefined, after: TypeConfig): string {
  if (!before) return "";
  return TYPE_FIELD_LABELS
    .filter(([k]) => (before[k] ?? null) !== (after[k] ?? null))
    .map(([k, label]) => `${label}: ${fmtTypeValue(before[k])} → ${fmtTypeValue(after[k])}`)
    .join(", ");
}

function LeaveTypesTab({ configs, reload }: { configs: TypeConfig[]; reload: () => void }) {
  const leaveTypes = useLookup("LEAVE_TYPE");
  const toast = useToast();
  const [localConfigs, setLocalConfigs] = useState<TypeConfig[]>(configs);
  const scroll = useHorizontalScrollEdges([localConfigs.length]);
  // Codes with unsaved local edits -- a background reload (e.g. triggered by saving a
  // DIFFERENT row) must not clobber these until they're actually saved.
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ code: "", displayName: "" });
  const [addSaving, setAddSaving] = useState(false);

  useEffect(() => {
    setLocalConfigs(prev => configs.map(c => {
      if (!dirty.has(c.code)) return c;
      return prev.find(p => p.code === c.code) ?? c;
    }));
  }, [configs, dirty]);

  function update(code: string, patch: Partial<TypeConfig>) {
    setLocalConfigs(cs => cs.map(c => c.code === code ? { ...c, ...patch } : c));
    setDirty(d => new Set(d).add(code));
  }

  async function save(code: string) {
    const cfg = localConfigs.find(c => c.code === code);
    if (!cfg) throw new Error("validation");
    const display = leaveTypes.find(t => t.value === code)?.displayName ?? code;
    const changes = describeTypeChanges(configs.find(c => c.code === code), cfg);
    try {
      await api.patch(`/admin/leave/type-config/${code}`, {
        trackBalance:         cfg.trackBalance,
        genderRestriction:    cfg.genderRestriction || null,
        maritalStatusRestriction: cfg.maritalStatusRestriction || null,
        isProbationBlocked:   cfg.isProbationBlocked,
        isNoticeBlocked:      cfg.isNoticeBlocked,
        isPipBlocked:         cfg.isPipBlocked,
        forceWfh:             cfg.forceWfh,
        maxDaysPerRequest:    cfg.maxDaysPerRequest ?? null,
        attachmentRequiredAboveDays: cfg.attachmentRequiredAboveDays ?? null,
        requiresApproverRole: cfg.requiresApproverRole,
        isEnabled:            cfg.isEnabled,
      });
      setDirty(d => { const n = new Set(d); n.delete(code); return n; });
      toast.show(changes ? `${display} updated — ${changes}` : `${display} saved`, "success");
      reload();
    } catch (err) {
      logError("LeaveTypesTab.save", err);
      toast.show(`Could not save ${display}: ${errMsg(err) ?? "please try again"}`, "error");
      throw err;
    }
  }

  async function submitAdd() {
    const code = addForm.code.trim().toUpperCase();
    if (!code || !addForm.displayName.trim()) return;
    setAddSaving(true);
    try {
      await api.post("/admin/lookup", { category: "LEAVE_TYPE", value: code, displayName: addForm.displayName.trim() });
      await api.post("/admin/leave/type-config", { code });
      window.location.reload(); // LookupContext only fetches once on mount -- a full reload is the
                                 // simplest way to make the new type immediately selectable everywhere.
    } catch (err) {
      logError("LeaveTypesTab.submitAdd", err);
      toast.show(errMsg(err) ?? "Failed to create leave type", "error");
      setAddSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="min-w-[240px] flex-1" style={{ fontSize: 13, color: "var(--color-surface-500)" }}>
          These flags control eligibility gates. Changes take effect immediately for new leave requests. A disabled type is hidden from Apply and rejected for new requests.
        </p>
        <button className="shrink-0 whitespace-nowrap" style={btn} onClick={() => { setAddForm({ code: "", displayName: "" }); setShowAddModal(true); }}>+ Add Leave Type</button>
      </div>
      {/* Wider than most screens, so the wrapper scrolls sideways. Code (left) and Save
          (right) are pinned so the row's name and its Save button stay in view, with arrows
          to jump to either end -- the same pattern as the Performance review list. */}
      <div ref={scroll.ref} className="overflow-x-auto rounded-lg border border-surface-200 bg-surface-0">
        <Table className="min-w-[1180px]">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 w-36 bg-surface-50 pl-4">
                <span className="flex items-center gap-1.5">
                  {scroll.canScrollLeft && (
                    <SpecularButton onClick={scroll.scrollToStart} aria-label="Scroll left back to the start" size="sm" className="w-6 px-0">
                      <ChevronsLeft size={13} />
                    </SpecularButton>
                  )}
                  Code
                </span>
              </TableHead>
              {["Display Name", "Enabled", "Track Balance", "Gender", "Marital Status", "Probation Block", "Notice Block", "PIP Block", "Force WFH", "Max Days/Req", "Attach Above (days)", "Approver Role"].map(h => (
                <TableHead key={h} className="whitespace-nowrap">{h}</TableHead>
              ))}
              <TableHead className="sticky right-0 z-10 w-24 bg-surface-50 pr-4 text-right">
                {scroll.canScrollRight && (
                  <SpecularButton onClick={scroll.scrollToEnd} aria-label="Scroll right to see more columns" size="sm" className="w-7 px-0">
                    <ChevronsRight size={14} />
                  </SpecularButton>
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localConfigs.map(cfg => {
              const display = leaveTypes.find(t => t.value === cfg.code)?.displayName ?? cfg.code;
              return (
                <TableRow key={cfg.code} className="group hover:bg-surface-50">
                  <TableCell className="sticky left-0 z-10 bg-surface-0 pl-4 font-mono text-[12px] font-semibold group-hover:bg-surface-50">{cfg.code}</TableCell>
                  <TableCell className="whitespace-nowrap">{display}</TableCell>
                  <TableCell><Toggle checked={cfg.isEnabled} onChange={v => update(cfg.code, { isEnabled: v })} /></TableCell>
                  <TableCell><Toggle checked={cfg.trackBalance} onChange={v => update(cfg.code, { trackBalance: v })} /></TableCell>
                  <TableCell>
                    <SelectField size="sm" className="w-[100px]"
                      value={cfg.genderRestriction ?? ""}
                      onChange={e => update(cfg.code, { genderRestriction: e.target.value || null })}>
                      <option value="">Any</option>
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                    </SelectField>
                  </TableCell>
                  <TableCell>
                    <SelectField size="sm" className="w-[100px]"
                      value={cfg.maritalStatusRestriction ?? ""}
                      onChange={e => update(cfg.code, { maritalStatusRestriction: e.target.value || null })}>
                      <option value="">Any</option>
                      <option value="Married">Married</option>
                      <option value="Single">Single</option>
                    </SelectField>
                  </TableCell>
                  <TableCell><Toggle checked={cfg.isProbationBlocked} onChange={v => update(cfg.code, { isProbationBlocked: v })} /></TableCell>
                  <TableCell><Toggle checked={cfg.isNoticeBlocked} onChange={v => update(cfg.code, { isNoticeBlocked: v })} /></TableCell>
                  <TableCell><Toggle checked={cfg.isPipBlocked} onChange={v => update(cfg.code, { isPipBlocked: v })} /></TableCell>
                  <TableCell><Toggle checked={cfg.forceWfh} onChange={v => update(cfg.code, { forceWfh: v })} /></TableCell>
                  <TableCell>
                    <Input className="w-[70px]" type="number" min="1" placeholder="∞"
                      value={cfg.maxDaysPerRequest ?? ""}
                      onChange={e => update(cfg.code, { maxDaysPerRequest: e.target.value ? Number(e.target.value) : null })} />
                  </TableCell>
                  <TableCell>
                    <Input className="w-[70px]" type="number" min="1" placeholder="∞"
                      value={cfg.attachmentRequiredAboveDays ?? ""}
                      onChange={e => update(cfg.code, { attachmentRequiredAboveDays: e.target.value ? Number(e.target.value) : null })} />
                  </TableCell>
                  <TableCell>
                    <SelectField size="sm" className="w-[120px]"
                      value={cfg.requiresApproverRole}
                      onChange={e => update(cfg.code, { requiresApproverRole: e.target.value })}>
                      <option value="MANAGER">Manager</option>
                      <option value="TEAM_LEAD">Team Lead</option>
                      <option value="REGIONAL_HR">Regional HR</option>
                      <option value="CORPORATE_HR">Corporate HR</option>
                      <option value="SUPER_HR_ADMIN">Super HR</option>
                    </SelectField>
                  </TableCell>
                  <TableCell className="sticky right-0 z-10 bg-surface-0 pr-4 text-right group-hover:bg-surface-50">
                    <AsyncButton size="sm" onClick={() => save(cfg.code)}>Save</AsyncButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {showAddModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 28, width: 420 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Add Leave Type</h3>
            <p style={{ fontSize: 12, color: "var(--color-surface-500)", marginBottom: 16 }}>
              Creates the type with default rules (no restrictions, Manager approval) — edit them in the table right after.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={lbl}>Code * <span style={{ fontWeight: 400, color: "var(--color-surface-400)" }}>(e.g. SABBATICAL)</span></label>
                <input style={{ ...inp, textTransform: "uppercase" }} value={addForm.code} onChange={e => setAddForm(f => ({ ...f, code: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Display Name *</label>
                <input style={inp} value={addForm.displayName} onChange={e => setAddForm(f => ({ ...f, displayName: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button style={btn} onClick={submitAdd} disabled={addSaving || !addForm.code.trim() || !addForm.displayName.trim()}>
                {addSaving ? "Creating…" : "Create"}
              </button>
              <button style={ghost} onClick={() => setShowAddModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Allocation Rules ────────────────────────────────────────────────────

function AllocationTab({ policies, reload }: { policies: LeavePolicy[]; reload: () => void }) {
  const leaveTypes = useLookup("LEAVE_TYPE");
  const [selected, setSelected]     = useState<string | null>(null);
  const [showModal, setShowModal]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [modalErr, setModalErr]     = useState("");

  const today = new Date().toISOString().split("T")[0]!;

  const [form, setForm] = useState({
    name: "", effectiveFrom: today, changeNote: "",
    daysAllowed: "18", carryForward: "5", minNoticeDays: "3", proRata: false,
    tiers: [] as { minTenureMonths: number; daysAllowed: number }[],
  });

  const countryOptions = Array.from(new Set([...policies.map(p => p.country), "India", "USA"]));
  const employeeTypeOptions = Array.from(new Set([...policies.map(p => p.employeeType), "FULL_TIME", "PART_TIME", "CONTRACTOR", "INTERN"]));
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [selectedEmployeeType, setSelectedEmployeeType] = useState("FULL_TIME");

  const grouped = leaveTypes.reduce<Record<string, LeavePolicy[]>>((acc, t) => {
    acc[t.value] = policies.filter(p =>
      p.leaveType === t.value && p.country === selectedCountry && p.employeeType === selectedEmployeeType
    ).sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime());
    return acc;
  }, {});

  // In force today -- a version with no end date may still be a scheduled future one.
  const isInForce = (p: LeavePolicy) =>
    new Date(p.effectiveFrom) <= new Date() && (!p.effectiveTo || p.effectiveTo.slice(0, 10) >= today);

  const displayHistory = selected ? (grouped[selected] ?? []) : [];
  const currentPolicy  = displayHistory.find(isInForce) ?? displayHistory[0];

  function openModal() {
    if (!currentPolicy) {
      // First version for this type / country / employee type.
      const displayName = leaveTypes.find(t => t.value === selected)?.displayName ?? selected ?? "";
      setForm({
        name: `${displayName} Policy`, effectiveFrom: today, changeNote: "Initial policy",
        daysAllowed: "0", carryForward: "0", minNoticeDays: "0", proRata: false, tiers: [],
      });
      setModalErr("");
      setShowModal(true);
      return;
    }
    setForm({
      name: currentPolicy.name, effectiveFrom: today, changeNote: "",
      daysAllowed: String(Number(currentPolicy.daysAllowed)),
      carryForward: String(Number(currentPolicy.carryForward)),
      minNoticeDays: String(currentPolicy.minNoticeDays),
      proRata: currentPolicy.proRata,
      tiers: currentPolicy.tiers.map(t => ({ minTenureMonths: t.minTenureMonths, daysAllowed: Number(t.daysAllowed) })),
    });
    setModalErr("");
    setShowModal(true);
  }

  async function submitVersion() {
    if (!selected) throw new Error("validation");
    if (!form.changeNote.trim()) { setModalErr("Change note is required"); throw new Error("validation"); }
    setSaving(true); setModalErr("");
    try {
      await api.post("/admin/leave/policies/version", {
        leaveType:     selected,
        country:       selectedCountry,
        employeeType:  selectedEmployeeType,
        name:          form.name,
        effectiveFrom: form.effectiveFrom,
        changeNote:    form.changeNote,
        daysAllowed:   Number(form.daysAllowed),
        carryForward:  Number(form.carryForward),
        minNoticeDays: Number(form.minNoticeDays),
        proRata:       form.proRata,
        tiers:         form.tiers.map(t => ({ minTenureMonths: t.minTenureMonths, daysAllowed: Number(t.daysAllowed) })),
      });
      setShowModal(false);
      reload();
    } catch (e: unknown) {
      const msg = errMsg(e) ?? "Save failed";
      setModalErr(msg);
      throw e;
    } finally { setSaving(false); }
  }

  function addTier() {
    setForm(f => ({ ...f, tiers: [...f.tiers, { minTenureMonths: 0, daysAllowed: 0 }] }));
  }

  function updateTier(i: number, patch: Partial<{ minTenureMonths: number; daysAllowed: number }>) {
    setForm(f => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  }

  function removeTier(i: number) {
    setForm(f => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  }

  const tierPreview = form.tiers.length > 0
    ? form.tiers.map(t => `${t.minTenureMonths}m → ${t.daysAllowed}d`).join(" | ")
    : `flat ${form.daysAllowed} days/year`;

  function fmtDate(s: string) {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }

  const schedBadge = (p: LeavePolicy) => {
    if (new Date(p.effectiveFrom) > new Date()) return <Badge label="Scheduled" color="var(--color-info-accent)" />;
    if (isInForce(p)) return <Badge label="Current" color="var(--color-success-accent)" />;
    return <Badge label="Past" color="var(--color-text-secondary)" />;
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div>
          <label style={lbl}>Country</label>
          <SelectField size="sm" specular className="w-[140px]" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
            {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </SelectField>
        </div>
        <div>
          <label style={lbl}>Employee Type</label>
          <SelectField size="sm" specular className="w-[160px]" value={selectedEmployeeType} onChange={e => setSelectedEmployeeType(e.target.value)}>
            {employeeTypeOptions.map(e => <option key={e} value={e}>{e}</option>)}
          </SelectField>
        </div>
      </div>
    <div style={{ display: "flex", gap: 20 }}>
      <div style={{ width: 200, flexShrink: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Leave Type</p>
        {leaveTypes.map(t => (
          <button key={t.value} type="button"
            onClick={() => setSelected(t.value)}
            style={{
              display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
              marginBottom: 4, borderRadius: "var(--radius-md)", border: "1.5px solid",
              borderColor: selected === t.value ? "var(--color-brand-500)" : "var(--color-surface-200)",
              background: selected === t.value ? "var(--color-brand-50, #eff6ff)" : "var(--color-surface-0)",
              cursor: "pointer", fontSize: 13,
            }}
          >
            {t.displayName}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected && (
          <div style={{ ...card, textAlign: "center", padding: 40, color: "var(--color-surface-400)" }}>
            Select a leave type to view its allocation rules
          </div>
        )}
        {selected && (
          <>
            <div className="flex-wrap gap-3" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ fontWeight: 600, fontSize: 15 }}>
                {leaveTypes.find(t => t.value === selected)?.displayName ?? selected}
              </p>
              <button style={btn} onClick={openModal}>{currentPolicy ? "+ New Version" : "+ Create Policy"}</button>
            </div>

            {displayHistory.length === 0 && (
              <div style={{ ...card, textAlign: "center", color: "var(--color-surface-400)", padding: 32 }}>No policy configured for this type yet.</div>
            )}

            {displayHistory.map((p, i) => (
              <div key={p.id} style={{ ...card, marginBottom: 12, borderLeft: isInForce(p) ? "4px solid var(--color-success)" : "4px solid var(--color-surface-200)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {schedBadge(p)}
                    <span style={{ fontSize: 12, color: "var(--color-surface-500)" }}>
                      {fmtDate(p.effectiveFrom)}
                      {p.effectiveTo ? ` – ${fmtDate(p.effectiveTo)}` : " – present"}
                    </span>
                  </div>
                  {i === 0 && p.changeNote && (
                    <span style={{ fontSize: 11, color: "var(--color-surface-400)", fontStyle: "italic" }}>
                      {p.changeNote}
                    </span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 8 }}>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase" }}>Days/Year</p>
                    <p style={{ fontSize: 20, fontWeight: 700 }}>{Number(p.daysAllowed)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase" }}>Max Carry</p>
                    <p style={{ fontSize: 20, fontWeight: 700 }}>{Number(p.carryForward)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase" }}>Min Notice</p>
                    <p style={{ fontSize: 20, fontWeight: 700 }}>{p.minNoticeDays}d</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase" }}>Pro-Rata</p>
                    <p style={{ fontSize: 14, fontWeight: 600 }}>{p.proRata ? "Yes" : "No"}</p>
                  </div>
                </div>

                {p.tiers.length > 0 && (
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", marginBottom: 6 }}>Tenure Tiers</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {p.tiers.map(t => (
                        <span key={t.minTenureMonths} style={{ fontSize: 12, padding: "3px 8px", background: "var(--color-surface-100)", borderRadius: 6 }}>
                          {t.minTenureMonths === 0 ? "At hire" : `After ${t.minTenureMonths}m`} → {Number(t.daysAllowed)}d
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 28, width: 560, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>New Policy Version</h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Policy Name *</label>
                <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Effective From *</label>
                <input style={inp} type="date" min={today} value={form.effectiveFrom} onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Days / Year</label>
                <input style={inp} type="number" min="0" step="0.5" value={form.daysAllowed} onChange={e => setForm(f => ({ ...f, daysAllowed: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Max Carry Forward</label>
                <input style={inp} type="number" min="0" step="0.5" value={form.carryForward} onChange={e => setForm(f => ({ ...f, carryForward: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Min Notice Days</label>
                <input style={inp} type="number" min="0" step="1" value={form.minNoticeDays} onChange={e => setForm(f => ({ ...f, minNoticeDays: e.target.value }))} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                <Toggle checked={form.proRata} onChange={v => setForm(f => ({ ...f, proRata: v }))} />
                <label style={{ fontSize: 13 }}>Pro-rata for new joiners</label>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Change Note * <span style={{ fontWeight: 400, color: "var(--color-surface-400)" }}>(reason for this version)</span></label>
                <input style={inp} placeholder="e.g. Increased EL to 15 days for senior employees per new policy" value={form.changeNote} onChange={e => setForm(f => ({ ...f, changeNote: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <p style={{ fontSize: 13, fontWeight: 600 }}>Tenure Tiers <span style={{ fontWeight: 400, color: "var(--color-surface-400)" }}>(optional)</span></p>
                <button style={btnSm} type="button" onClick={addTier}>+ Add Tier</button>
              </div>
              {form.tiers.length === 0 && (
                <p style={{ fontSize: 12, color: "var(--color-surface-400)" }}>No tiers — flat {form.daysAllowed} days/year for all tenure lengths.</p>
              )}
              {form.tiers.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Min Tenure (months)</label>
                    <input style={inp} type="number" min="0" value={t.minTenureMonths}
                      onChange={e => updateTier(i, { minTenureMonths: Number(e.target.value) })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Days Allowed</label>
                    <input style={inp} type="number" min="0" step="0.5" value={t.daysAllowed}
                      onChange={e => updateTier(i, { daysAllowed: Number(e.target.value) })} />
                  </div>
                  <button type="button" onClick={() => removeTier(i)}
                    style={{ ...btnSm, background: "var(--color-error-bg)", color: "var(--color-error-fg)", marginTop: 16, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><X size={12} /></button>
                </div>
              ))}
              {form.tiers.length > 0 && (
                <p style={{ fontSize: 11, color: "var(--color-surface-500)", marginTop: 8 }}>Preview: {tierPreview}</p>
              )}
            </div>

            {modalErr && <p style={{ color: "var(--color-error)", fontSize: 12, marginBottom: 12 }}>{modalErr}</p>}
            {form.effectiveFrom === today && (
              <p style={{ fontSize: 12, color: "var(--color-warning-fg-strong)", background: "var(--color-warning-bg)", padding: "6px 10px", borderRadius: 6, marginBottom: 12 }}>
                Effective today — balances move to this version automatically in tonight's run (03:45 IST), keeping leave already taken.
              </p>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <AsyncButton onClick={() => submitVersion()} disabled={saving}>Create Version</AsyncButton>
              <button style={ghost} onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

// ── Tab 3: Blackout Periods ────────────────────────────────────────────────────

function BlackoutTab({ blackouts, reload }: { blackouts: Blackout[]; reload: () => void }) {
  const leaveTypes = useLookup("LEAVE_TYPE");
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editId,    setEditId]    = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [deleting,  setDeleting]  = useState<string | null>(null);

  const blank = { name: "", country: "India", startMonth: 1, startDay: 1, endMonth: 1, endDay: 31, year: "", leaveTypeCodes: [] as string[], isActive: true };
  const [form, setForm] = useState(blank);

  function openAdd()  { setForm(blank); setEditId(null); setShowModal(true); }
  function openEdit(b: Blackout) {
    let codes: string[] = [];
    try { codes = JSON.parse(b.leaveTypeCodes) as string[]; } catch { codes = []; }
    setForm({ name: b.name, country: b.country, startMonth: b.startMonth, startDay: b.startDay, endMonth: b.endMonth, endDay: b.endDay, year: b.year ? String(b.year) : "", leaveTypeCodes: codes, isActive: b.isActive });
    setEditId(b.id);
    setShowModal(true);
  }

  async function submit() {
    setSaving(true);
    const payload = { ...form, year: form.year ? Number(form.year) : null };
    try {
      if (editId) {
        await api.patch(`/admin/leave/blackouts/${editId}`, payload);
      } else {
        await api.post("/admin/leave/blackouts", payload);
      }
      setShowModal(false);
      reload();
    } catch (err) {
      logError("BlackoutTab.submit", err);
      toast.show(errMsg(err) ?? "Failed to save blackout period", "error");
      throw err;
    }
    finally { setSaving(false); }
  }

  async function del(id: string, name: string) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try { await api.delete(`/admin/leave/blackouts/${id}`); reload(); }
    catch (err) {
      logError("BlackoutTab.del", err);
      toast.show(errMsg(err) ?? "Failed to delete blackout period", "error");
    }
    finally { setDeleting(null); }
  }

  function toggleCode(code: string) {
    setForm(f => ({
      ...f,
      leaveTypeCodes: f.leaveTypeCodes.includes(code)
        ? f.leaveTypeCodes.filter(c => c !== code)
        : [...f.leaveTypeCodes, code],
    }));
  }

  function fmtRange(b: Blackout) {
    return `${MONTH_NAMES[b.startMonth - 1]} ${b.startDay} – ${MONTH_NAMES[b.endMonth - 1]} ${b.endDay}${b.year ? ` ${b.year}` : " (recurring)"}`;
  }

  function parseCodes(s: string): string[] {
    try { return JSON.parse(s) as string[]; } catch { return []; }
  }

  return (
    <div>
      <div className="flex-wrap gap-3" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "var(--color-surface-500)" }}>Block specific date ranges for leave applications.</p>
        <button style={btn} onClick={openAdd}>+ Add Blackout</button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--color-surface-50)" }}>
            {["Name","Country","Date Range","Applies To","Active","Actions"].map(h => (
              <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {blackouts.map(b => {
            const codes = parseCodes(b.leaveTypeCodes);
            return (
              <tr key={b.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{b.name}</td>
                <td style={{ padding: "10px 12px" }}>{b.country}</td>
                <td style={{ padding: "10px 12px", fontSize: 12 }}>{fmtRange(b)}</td>
                <td style={{ padding: "10px 12px", fontSize: 12 }}>
                  {codes.length === 0 ? <span style={{ color: "var(--color-surface-400)" }}>All types</span>
                    : codes.map(c => <span key={c} style={{ marginRight: 4, fontSize: 11, padding: "1px 5px", background: "var(--color-surface-100)", borderRadius: 4 }}>{c}</span>)}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, background: b.isActive ? "var(--color-success-bg)" : "var(--color-error-bg)", color: b.isActive ? "var(--color-success-fg)" : "var(--color-error-fg)" }}>
                    {b.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={btnSm} onClick={() => openEdit(b)}>Edit</button>
                    <button style={{ ...btnSm, background: "var(--color-error-bg)", color: "var(--color-error-fg)" }} onClick={() => del(b.id, b.name)} disabled={deleting === b.id}>
                      {deleting === b.id ? "…" : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {blackouts.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--color-surface-400)" }}>No blackout periods configured.</td></tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 28, width: 500, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{editId ? "Edit" : "New"} Blackout Period</h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Name *</label>
                <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Start Month / Day</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <SelectField size="sm" className="flex-1 min-w-0" value={form.startMonth} onChange={e => setForm(f => ({ ...f, startMonth: Number(e.target.value) }))}>
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </SelectField>
                  <input style={{ ...inp, width: 60 }} type="number" min="1" max="31" value={form.startDay} onChange={e => setForm(f => ({ ...f, startDay: Number(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label style={lbl}>End Month / Day</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <SelectField size="sm" className="flex-1 min-w-0" value={form.endMonth} onChange={e => setForm(f => ({ ...f, endMonth: Number(e.target.value) }))}>
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </SelectField>
                  <input style={{ ...inp, width: 60 }} type="number" min="1" max="31" value={form.endDay} onChange={e => setForm(f => ({ ...f, endDay: Number(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label style={lbl}>Year <span style={{ fontWeight: 400, color: "var(--color-surface-400)" }}>(blank = recurring)</span></label>
                <input style={inp} type="number" min="2024" max="2099" placeholder="Recurring" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                <Toggle checked={form.isActive} onChange={v => setForm(f => ({ ...f, isActive: v }))} />
                <label style={{ fontSize: 13 }}>Active</label>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Applies to leave types <span style={{ fontWeight: 400, color: "var(--color-surface-400)" }}>(none = all types)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                  {leaveTypes.map(t => (
                    <button key={t.value} type="button" onClick={() => toggleCode(t.value)}
                      style={{
                        padding: "3px 10px", border: "1.5px solid",
                        borderColor: form.leaveTypeCodes.includes(t.value) ? "var(--color-brand-500)" : "var(--color-surface-200)",
                        background: form.leaveTypeCodes.includes(t.value) ? "var(--color-brand-50, #eff6ff)" : "transparent",
                        borderRadius: 999, fontSize: 12, cursor: "pointer",
                      }}>
                      {t.displayName}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <AsyncButton onClick={() => submit()} disabled={saving || !form.name}>Save</AsyncButton>
              <button style={ghost} onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 4: WFH & Departments ──────────────────────────────────────────────────

function WFHTab({ departments, reload }: { departments: Department[]; reload: () => void }) {
  const toast = useToast();
  const [toggling, setToggling] = useState<string | null>(null);

  async function toggle(dept: Department) {
    setToggling(dept.id);
    try {
      await api.patch(`/admin/departments/${dept.id}/wfh-eligible`, { wfhEligible: !dept.wfhEligible });
      reload();
    } catch (err) {
      logError("WFHTab.toggle", err);
      toast.show(errMsg(err) ?? "Failed to update WFH eligibility", "error");
    }
    finally { setToggling(null); }
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--color-surface-500)", marginBottom: 16 }}>
        Toggle WFH eligibility per department. WFH leave requests from ineligible departments are automatically rejected.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {departments.map(d => (
          <div key={d.id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14 }}>{d.name}</p>
              <p style={{ fontSize: 12, color: "var(--color-surface-400)" }}>{d.code}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: d.wfhEligible ? "var(--color-success)" : "var(--color-error)" }}>
                {d.wfhEligible ? "WFH Eligible" : "No WFH"}
              </span>
              {toggling === d.id
                ? <span style={{ fontSize: 12, color: "var(--color-surface-400)" }}>…</span>
                : <Toggle checked={d.wfhEligible} onChange={() => toggle(d)} />
              }
            </div>
          </div>
        ))}
        {departments.length === 0 && (
          <p style={{ color: "var(--color-surface-400)", fontSize: 13 }}>No departments found.</p>
        )}
      </div>
    </div>
  );
}

// ── Tab 5: National Holidays & Comoff ─────────────────────────────────────────

// Mirrors comoffDaysFor in leave-policy.service.ts: a national holiday (26 Jan, 15 Aug,
// 2 Oct are flagged automatically) earns 2 comp-off days, any other holiday 1; USA is 1.
function comoffDays(h: Holiday): number {
  if (h.country === "USA") return 1;
  return h.isNational ? 2 : 1;
}

function HolidaysTab({ holidays, reload }: { holidays: Holiday[]; reload: () => void }) {
  const toast = useToast();
  const [toggling,    setToggling]    = useState<string | null>(null);
  const [showComoff,  setShowComoff]  = useState<Holiday | null>(null);
  const [employees,   setEmployees]   = useState<Employee[]>([]);
  const [comoffEmpId, setComoffEmpId] = useState("");
  const [comoffNote,  setComoffNote]  = useState("");
  const [saving,      setSaving]      = useState(false);

  const year = new Date().getFullYear();
  const yearHolidays = holidays.filter(h => h.year === year).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  async function toggleNational(h: Holiday) {
    setToggling(h.id);
    try { await api.patch(`/admin/holidays/${h.id}/national`, { isNational: !h.isNational }); reload(); }
    catch (err) {
      logError("HolidaysTab.toggle", err);
      toast.show(errMsg(err) ?? "Failed to update holiday", "error");
    }
    finally { setToggling(null); }
  }

  async function openComoff(h: Holiday) {
    setShowComoff(h);
    setComoffEmpId(""); setComoffNote("");
    try {
      const r = await api.get<{ data: Employee[] }>("/employees?status=ACTIVE&limit=200");
      setEmployees(r.data.data ?? []);
    } catch (err) {
      logError("HolidaysTab.loadEmps", err);
      toast.show(errMsg(err) ?? "Failed to load employees", "error");
    }
  }

  async function creditComoff() {
    if (!showComoff || !comoffEmpId) throw new Error("validation");
    setSaving(true);
    try {
      await api.post("/admin/leave/credit-comoff", {
        employeeId: comoffEmpId,
        holidayId:  showComoff.id,
        note:       comoffNote || undefined,
      });
      const emp = employees.find(e => e.id === comoffEmpId);
      const days = comoffDays(showComoff);
      toast.show(`Credited ${days} comp-off day${days === 1 ? "" : "s"} to ${emp ? `${emp.firstName} ${emp.lastName}` : "the employee"} for ${showComoff.name}`, "success");
      setShowComoff(null);
    } catch (err) {
      logError("HolidaysTab.creditComoff", err);
      toast.show(errMsg(err) ?? "Failed to credit comp-off", "error");
      throw err;
    }
    finally { setSaving(false); }
  }

  function fmtDate(s: string) {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--color-surface-500)", marginBottom: 16 }}>
        Credit comp-off to employees who worked on a holiday: 2 days for a national holiday (26 Jan, 15 Aug, 2 Oct), 1 day for any other holiday. Each employee can be credited once per holiday.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--color-surface-50)" }}>
            {["Date","Holiday","National?","Optional","Comoff"].map(h => (
              <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {yearHolidays.map(h => (
            <tr key={h.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
              <td style={{ padding: "10px 12px", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtDate(h.date)}</td>
              <td style={{ padding: "10px 12px" }}>{h.name}</td>
              <td style={{ padding: "10px 12px" }}>
                {toggling === h.id
                  ? <span style={{ fontSize: 12 }}>…</span>
                  : <Toggle checked={h.isNational} onChange={() => toggleNational(h)} />
                }
              </td>
              <td style={{ padding: "10px 12px" }}>
                <span style={{ fontSize: 12, color: h.isOptional ? "var(--color-brand-500)" : "var(--color-surface-400)" }}>
                  {h.isOptional ? "Optional" : "—"}
                </span>
              </td>
              <td style={{ padding: "10px 12px" }}>
                <Button size="sm" variant="outline" onClick={() => openComoff(h)}>
                  Credit {comoffDays(h)} day{comoffDays(h) === 1 ? "" : "s"}
                </Button>
              </td>
            </tr>
          ))}
          {yearHolidays.length === 0 && (
            <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "var(--color-surface-400)" }}>No holidays for {year}.</td></tr>
          )}
        </tbody>
      </table>

      {showComoff && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 28, width: 420 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Credit Comp-Off</h3>
            <p style={{ fontSize: 13, color: "var(--color-surface-500)", marginBottom: 20 }}>
              For working on <strong>{showComoff.name}</strong> ({fmtDate(showComoff.date)})
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={lbl}>Employee *</label>
                <SelectField size="sm" value={comoffEmpId} onChange={e => setComoffEmpId(e.target.value)}>
                  <option value="">Select employee…</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeCode})</option>
                  ))}
                </SelectField>
              </div>
              <div>
                <label style={lbl}>Days to credit</label>
                <p style={{ fontSize: 13, color: "var(--color-surface-700)", margin: 0 }}>
                  Credits {comoffDays(showComoff)} day{comoffDays(showComoff) === 1 ? "" : "s"} ({showComoff.isNational ? "national holiday" : "holiday"}, {showComoff.country}). Unused days lapse 90 days after the holiday.
                </p>
              </div>
              <div>
                <label style={lbl}>Note (optional)</label>
                <input style={inp} value={comoffNote} onChange={e => setComoffNote(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <AsyncButton onClick={() => creditComoff()} disabled={saving || !comoffEmpId}>Credit</AsyncButton>
              <button style={ghost} onClick={() => setShowComoff(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 6: Employee Balances ──────────────────────────────────────────────────

function EmployeeBalanceTab() {
  const toast = useToast();
  const leaveTypes = useLookup("LEAVE_TYPE");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeId, setEmployeeId] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [balances, setBalances] = useState<{ leaveType: string; allocated: string; used: string; pending: string; carriedIn: string; accruedOverride: string | null; accrued: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ leaveType: "", allocated: "0", used: "0", pending: "0", carriedIn: "0", accruedOverride: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ data: Employee[] }>("/employees?status=ACTIVE&limit=200")
      .then(r => setEmployees(r.data.data ?? []))
      .catch(err => { logError("EmployeeBalanceTab.loadEmps", err); toast.show(errMsg(err) ?? "Failed to load employees", "error"); });
  }, [toast.show]);

  const loadBalances = useCallback(() => {
    if (!employeeId) { setBalances([]); return; }
    setLoading(true);
    api.get<{ leaveType: string; allocated: string; used: string; pending: string; carriedIn: string; accruedOverride: string | null; accrued: string }[]>(`/leave/balance/${employeeId}?year=${year}`)
      .then(r => setBalances(r.data))
      .catch(err => { logError("EmployeeBalanceTab.loadBalances", err); toast.show(errMsg(err) ?? "Failed to load balances", "error"); })
      .finally(() => setLoading(false));
  }, [employeeId, year, toast.show]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  function selectLeaveTypeToEdit(code: string) {
    const existing = balances.find(b => b.leaveType === code);
    setForm({
      leaveType:       code,
      allocated:       existing ? String(Number(existing.allocated)) : "0",
      used:            existing ? String(Number(existing.used))      : "0",
      pending:         existing ? String(Number(existing.pending))   : "0",
      carriedIn:       existing ? String(Number(existing.carriedIn)) : "0",
      accruedOverride: existing?.accruedOverride != null ? String(Number(existing.accruedOverride)) : "",
    });
  }

  async function submitAdjust() {
    if (!employeeId || !form.leaveType) throw new Error("validation");
    setSaving(true);
    try {
      await api.patch(`/admin/leave/balance/${employeeId}/${form.leaveType}/${year}`, {
        allocated: Number(form.allocated), used: Number(form.used), pending: Number(form.pending),
        carriedIn: Number(form.carriedIn),
        accruedOverride: form.accruedOverride.trim() === "" ? null : Number(form.accruedOverride),
      });
      toast.show("Balance updated", "success");
      loadBalances();
    } catch (err) {
      logError("EmployeeBalanceTab.submitAdjust", err);
      toast.show(errMsg(err) ?? "Failed to update balance", "error");
      throw err;
    } finally { setSaving(false); }
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--color-surface-500)", marginBottom: 16 }}>
        Manually correct or grant an individual employee's leave balance. Use sparingly — normal balances come from Seed Balances / Year Rollover.
      </p>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Employee</label>
          <SelectField size="sm" value={employeeId} onChange={e => { setEmployeeId(e.target.value); setForm({ leaveType: "", allocated: "0", used: "0", pending: "0", carriedIn: "0", accruedOverride: "" }); }}>
            <option value="">Select employee…</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeCode})</option>)}
          </SelectField>
        </div>
        <div>
          <label style={lbl}>Year</label>
          <SelectField size="sm" className="w-[100px]" value={year} onChange={e => setYear(e.target.value)}>
            {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </SelectField>
        </div>
      </div>

      {employeeId && (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 20 }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Leave Type", "Allocated", "Accrued", "Used", "Pending", "Carried In", ""].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "var(--color-surface-400)" }}>Loading…</td></tr>}
              {!loading && balances.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "var(--color-surface-400)" }}>No balances for {year} yet.</td></tr>
              )}
              {!loading && balances.map(b => (
                <tr key={b.leaveType} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12 }}>{b.leaveType}</td>
                  <td style={{ padding: "10px 12px" }}>{Number(b.allocated)}</td>
                  <td style={{ padding: "10px 12px" }}>{Number(b.accrued)}</td>
                  <td style={{ padding: "10px 12px" }}>{Number(b.used)}</td>
                  <td style={{ padding: "10px 12px" }}>{Number(b.pending)}</td>
                  <td style={{ padding: "10px 12px" }}>{Number(b.carriedIn)}</td>
                  <td style={{ padding: "10px 12px" }}><button style={btnSm} onClick={() => selectLeaveTypeToEdit(b.leaveType)}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ ...card }}>
            <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Adjust Balance</p>
            <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div>
                <label style={lbl}>Leave Type</label>
                <SelectField size="sm" className="w-[160px]" value={form.leaveType} onChange={e => selectLeaveTypeToEdit(e.target.value)}>
                  <option value="">Select…</option>
                  {leaveTypes.map(t => <option key={t.value} value={t.value}>{t.displayName}</option>)}
                </SelectField>
              </div>
              <div>
                <label style={lbl}>Allocated</label>
                <Input className="w-[90px]" type="number" step="0.01" min="0" value={form.allocated} onChange={e => setForm(f => ({ ...f, allocated: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Used</label>
                <Input className="w-[90px]" type="number" step="0.01" min="0" value={form.used} onChange={e => setForm(f => ({ ...f, used: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Pending</label>
                <Input className="w-[90px]" type="number" step="0.01" min="0" value={form.pending} onChange={e => setForm(f => ({ ...f, pending: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Carried In</label>
                <Input className="w-[90px]" type="number" step="0.01" min="0" value={form.carriedIn} onChange={e => setForm(f => ({ ...f, carriedIn: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Override Accrued (optional)</label>
                <Input className="w-[140px]" type="number" step="0.01" min="0" placeholder="Computed" value={form.accruedOverride} onChange={e => setForm(f => ({ ...f, accruedOverride: e.target.value }))} />
              </div>
            </div>
            <AsyncButton onClick={() => submitAdjust()} disabled={saving || !form.leaveType}>Save Balance</AsyncButton>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function LeaveAdminPage() {
  usePageBack("/admin", "Back to Admin");
  const { role } = useMe();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (role && !SUPER_HR.has(role)) navigate("/admin", { replace: true });
  }, [role, navigate]);

  const [tab,         setTab]         = useState<Tab>("Leave Types");
  const [configs,     setConfigs]     = useState<TypeConfig[]>([]);
  const [policies,    setPolicies]    = useState<LeavePolicy[]>([]);
  const [blackouts,   setBlackouts]   = useState<Blackout[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [holidays,    setHolidays]    = useState<Holiday[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [seedYear,    setSeedYear]    = useState(String(new Date().getFullYear()));
  const [seeding,     setSeeding]     = useState(false);
  const [seedResult,  setSeedResult]  = useState<{ seeded: number; skipped: number; employees: number } | null>(null);

  const thisYear = new Date().getFullYear();
  const [rolloverFromYear, setRolloverFromYear] = useState(String(thisYear));
  const [rolloverToYear,   setRolloverToYear]   = useState(String(thisYear + 1));
  const [rollingOver,      setRollingOver]      = useState(false);
  const [rolloverResult,   setRolloverResult]   = useState<{ employees: number; balancesCreated: number; carryTotal: number } | null>(null);

  const loadAll = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [cfgR, polR, blkR, deptR, holR] = await Promise.all([
        api.get<TypeConfig[]>("/leave/type-config"),
        api.get<LeavePolicy[]>("/admin/leave-policies"),
        api.get<Blackout[]>("/admin/leave/blackouts"),
        api.get<Department[]>("/admin/departments"),
        api.get<Holiday[]>("/admin/holidays?year=" + new Date().getFullYear()),
      ]);
      setConfigs(cfgR.data);
      setPolicies(polR.data);
      setBlackouts(blkR.data);
      setDepartments(deptR.data);
      setHolidays(holR.data);
    } catch (err) {
      logError("LeaveAdminPage.loadAll", err);
      toast.show(errMsg(err) ?? "Failed to load leave admin data", "error");
    }
    finally { if (!opts?.silent) setLoading(false); }
  }, [toast.show]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  async function seedBalances() {
    setSeeding(true); setSeedResult(null);
    try {
      const r = await api.post<{ seeded: number; skipped: number; employees: number }>("/admin/leave/seed-balances", { year: Number(seedYear) });
      setSeedResult(r.data);
    } catch (err) {
      logError("LeaveAdminPage.seedBalances", err);
      toast.show(errMsg(err) ?? "Failed to seed leave balances", "error");
      throw err;
    }
    finally { setSeeding(false); }
  }

  async function runRollover() {
    setRollingOver(true); setRolloverResult(null);
    try {
      const r = await api.post<{ employees: number; balancesCreated: number; carryTotal: number }>(
        "/admin/leave/year-rollover", { fromYear: Number(rolloverFromYear), toYear: Number(rolloverToYear) }
      );
      setRolloverResult(r.data);
    } catch (err) {
      logError("LeaveAdminPage.runRollover", err);
      toast.show(errMsg(err) ?? "Failed to run year rollover", "error");
      throw err;
    } finally { setRollingOver(false); }
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] p-4 sm:p-6 min-[1536px]:max-w-[1600px] min-[1920px]:max-w-[2000px]">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-[260px] flex-1">
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>Leave Admin</h1>
          <p style={{ fontSize: 13, color: "var(--color-surface-500)" }}>
            Configure all leave policies, type rules, blackout periods, WFH eligibility, and national holidays.
          </p>
        </div>
        <div className="flex max-w-full flex-col gap-1 sm:max-w-[420px] sm:items-end">
          <div className="flex flex-wrap items-center gap-2.5 sm:justify-end">
            <SelectField size="sm" className="w-[90px]" value={seedYear} onChange={e => setSeedYear(e.target.value)}>
              {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </SelectField>
            <AsyncButton variant="success" onClick={() => seedBalances()} disabled={seeding}>Seed Balances</AsyncButton>
            {seedResult && (
              <span style={{ fontSize: 12, color: "var(--color-success)" }}>
                Seeded {seedResult.seeded} balances across {seedResult.employees} employees ({seedResult.skipped} skipped)
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--color-surface-500)] sm:text-right">
            Doesn't correct a stale tier for employees who've already used leave this year — use Run Rollover for that.
          </p>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 20 }} className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-[240px] flex-1">
          <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Year-End Rollover</p>
          <p style={{ fontSize: 12, color: "var(--color-surface-500)" }}>Carry forward unused balances into the next year for all India employees.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SelectField size="sm" className="w-[90px]" value={rolloverFromYear} onChange={e => setRolloverFromYear(e.target.value)}>
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </SelectField>
          <span style={{ fontSize: 13, color: "var(--color-surface-400)" }}>→</span>
          <SelectField size="sm" className="w-[90px]" value={rolloverToYear} onChange={e => setRolloverToYear(e.target.value)}>
            {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </SelectField>
          <AsyncButton
            variant="success"
            onClick={() => (window.confirm(`Roll forward balances from ${rolloverFromYear} to ${rolloverToYear} for all India employees? This cannot be undone from this screen.`) ? runRollover() : undefined)}
            disabled={rollingOver}
          >
            Run Rollover
          </AsyncButton>
        </div>
      </div>
      {rolloverResult && (
        <p style={{ fontSize: 12, color: "var(--color-success)", marginTop: -12, marginBottom: 20 }}>
          Rolled over {rolloverResult.balancesCreated} balances (carrying forward {rolloverResult.carryTotal} total days) across {rolloverResult.employees} employees.
        </p>
      )}

      <div className="mb-6 flex gap-0.5 overflow-x-auto border-b border-surface-200">
        {TABS.map(t => (
          <button key={t} type="button" onClick={() => setTab(t)} className="shrink-0 whitespace-nowrap"
            style={{
              padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 600,
              borderBottom: tab === t ? "2px solid var(--color-brand-500)" : "2px solid transparent",
              color: tab === t ? "var(--color-brand-500)" : "var(--color-surface-600)",
              marginBottom: -1,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "var(--color-surface-400)", fontSize: 13 }}>Loading…</p>}

      {!loading && tab === "Leave Types"          && <LeaveTypesTab configs={configs} reload={() => loadAll({ silent: true })} />}
      {!loading && tab === "Allocation Rules"     && <AllocationTab policies={policies} reload={() => loadAll({ silent: true })} />}
      {!loading && tab === "Blackout Periods"     && <BlackoutTab blackouts={blackouts} reload={() => loadAll({ silent: true })} />}
      {!loading && tab === "WFH & Departments"    && <WFHTab departments={departments} reload={() => loadAll({ silent: true })} />}
      {!loading && tab === "National Holidays & Comoff" && <HolidaysTab holidays={holidays} reload={() => loadAll({ silent: true })} />}
      {!loading && tab === "Employee Balances" && <EmployeeBalanceTab />}
      {!loading && tab === "History"           && <LeaveChangeHistory />}
    </div>
  );
}
